/**
 * Text reveals with SplitText:
 *   [data-chars]  big statements, character by character
 *   [data-words]  leads and quotes, word by word, driven by the scroll
 *
 * SplitText needs the final font metrics, so it waits for the fonts. Until
 * then the elements are parked at opacity 0 — otherwise the text would show
 * in place and visibly jump when it gets split.
 *
 * @module motion/text
 */

import { REDUCED_MOTION } from '../config.js';
import { $$ } from '../lib/dom.js';
import { SplitText, gsap } from '../lib/gsap.js';

const SELECTOR = '[data-chars], [data-words]';

/** Parks the split targets until their start state exists. */
export function hideTextUntilSplit() {
    const elements = $$(SELECTOR);

    if (!REDUCED_MOTION && elements.length) {
        gsap.set(elements, { opacity: 0 });
    }
}

/** Word by word, scrubbed by the scroll position. */
function revealWords() {
    $$('[data-words]').forEach((element) => {
        const split = new SplitText(element, { type: 'words' });

        gsap.from(split.words, {
            opacity: 0.2,
            ease: 'none',
            stagger: 0.4,
            scrollTrigger: { trigger: element, start: 'top 84%', end: 'bottom 60%', scrub: true },
        });

        gsap.set(element, { opacity: 1 });
    });
}

/** Character stagger. No line mask: the hairline accent words would be clipped. */
function revealCharacters() {
    $$('[data-chars]').forEach((element) => {
        const split = new SplitText(element, { type: 'chars,words' });

        gsap.from(split.chars, {
            yPercent: 55,
            opacity: 0,
            duration: 0.9,
            stagger: { each: 0.02, from: 'start' },
            scrollTrigger: { trigger: element, start: 'top 90%', once: true },
        });

        gsap.set(element, { opacity: 1 });
    });
}

/** Splits and reveals the text; call once the fonts are in. */
export function animateText() {
    if (REDUCED_MOTION) {
        const elements = $$(SELECTOR);

        if (elements.length) {
            gsap.set(elements, { opacity: 1 });
        }

        return;
    }

    revealWords();
    revealCharacters();
}

/**
 * Safety net: text parked for SplitText must never stay hidden if the fonts
 * promise stalls.
 *
 * @param {number} delay - Milliseconds.
 */
export function unhideStuckText(delay) {
    window.setTimeout(() => {
        const stuck = $$(SELECTOR).filter((element) => Number(gsap.getProperty(element, 'opacity')) === 0);

        if (stuck.length) {
            gsap.set(stuck, { opacity: 1 });
        }
    }, delay);
}
